package dev.youndie.site.pages

import androidx.compose.runtime.*
import com.varabyte.kobweb.core.Page
import com.varabyte.kobweb.core.data.*
import com.varabyte.kobweb.core.layout.Layout
import com.varabyte.kobweb.core.layout.NoLayout
import com.varabyte.kobweb.core.init.InitRoute
import com.varabyte.kobweb.core.init.InitRouteContext
import com.varabyte.kobwebx.frontmatter.*
import com.varabyte.kobwebx.markdown.*

@InitRoute
fun initShildikPage(ctx: InitRouteContext) {
    ctx.data.add(MarkdownContext("shildik.md", FrontMatterElement.Builder { addScalar("title", "shildik"); addScalar("searchTitle", "OpenID Connect provider in Kotlin — Kotlin/Native, Ktor, sign-in methods as modules"); addScalar("description", "An OIDC provider that runs as one native binary against one Postgres, where a sign-in method is a module and a distribution is a main() you write yourself."); addScalar("library", "shildik"); addScalar("post", "/blog/an-identity-provider-for-forty-six-megabytes"); }))
}

@Page("/shildik")
@Layout(".components.layouts.LibraryLayout")
@Composable
fun ShildikPage() {
    org.jetbrains.compose.web.dom.H2(attrs = { id("why-it-exists") }) {
        org.jetbrains.compose.web.dom.Text("Why it exists")
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("A platform needed three things from Keycloak — service tokens, browser sign-in, and keys to verify them — and paid 640 MiB of memory for everything else. The part that hurt was not the memory. It was the magic link: in Keycloak it lived as a jar built against somebody else's SPI, loaded into somebody else's process, assembled by hand, with the sources eventually lost.")
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("Here a sign-in method is a module that implements one interface, and a distribution is a ")
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("main()")
        }
        org.jetbrains.compose.web.dom.Text(" that lists the ones it wants. What a build does ")
        org.jetbrains.compose.web.dom.B {
            org.jetbrains.compose.web.dom.Text("not")
        }
        org.jetbrains.compose.web.dom.Text(" carry is a fact of the build rather than a setting.")
    }
    org.jetbrains.compose.web.dom.H2(attrs = { id("what-you-get") }) {
        org.jetbrains.compose.web.dom.Text("What you get")
    }
    org.jetbrains.compose.web.dom.Ul {
        org.jetbrains.compose.web.dom.Li {
            org.jetbrains.compose.web.dom.B {
                org.jetbrains.compose.web.dom.Text("One native binary")
            }
            org.jetbrains.compose.web.dom.Text(" against one Postgres. 45–48 MiB resident in production, measured over a 20-hour window; the JVM build of the same code is kept as a rollback path.")
        }
        org.jetbrains.compose.web.dom.Li {
            org.jetbrains.compose.web.dom.B {
                org.jetbrains.compose.web.dom.Text("The OIDC a platform actually calls")
            }
            org.jetbrains.compose.web.dom.Text(": ")
            org.jetbrains.compose.web.dom.Code {
                org.jetbrains.compose.web.dom.Text("client_credentials")
            }
            org.jetbrains.compose.web.dom.Text(", JWKS, discovery, authorization code with PKCE, ")
            org.jetbrains.compose.web.dom.Code {
                org.jetbrains.compose.web.dom.Text("id_token")
            }
            org.jetbrains.compose.web.dom.Text(", refresh rotation, ")
            org.jetbrains.compose.web.dom.Code {
                org.jetbrains.compose.web.dom.Text("userinfo")
            }
            org.jetbrains.compose.web.dom.Text(", ")
            org.jetbrains.compose.web.dom.Code {
                org.jetbrains.compose.web.dom.Text("end_session")
            }
            org.jetbrains.compose.web.dom.Text(".")
        }
        org.jetbrains.compose.web.dom.Li {
            org.jetbrains.compose.web.dom.B {
                org.jetbrains.compose.web.dom.Text("Sign-in methods as modules")
            }
            org.jetbrains.compose.web.dom.Text(" — Google, magic link, password. A build without ")
            org.jetbrains.compose.web.dom.Code {
                org.jetbrains.compose.web.dom.Text("auth-password")
            }
            org.jetbrains.compose.web.dom.Text(" cannot be configured into having passwords.")
        }
        org.jetbrains.compose.web.dom.Li {
            org.jetbrains.compose.web.dom.B {
                org.jetbrains.compose.web.dom.Text("Two ports.")
            }
            org.jetbrains.compose.web.dom.Text(" The admin API lives on its own port and is never exposed; a request to ")
            org.jetbrains.compose.web.dom.Code {
                org.jetbrains.compose.web.dom.Text("/admin")
            }
            org.jetbrains.compose.web.dom.Text(" from outside gets a 404 rather than a 403, so the existence of a management contour is not confirmed.")
        }
        org.jetbrains.compose.web.dom.Li {
            org.jetbrains.compose.web.dom.B {
                org.jetbrains.compose.web.dom.Text("A CLI instead of a console.")
            }
            org.jetbrains.compose.web.dom.Text(" Clients, roles, users and signing keys, with the whole configuration exportable to git and applied back with ")
            org.jetbrains.compose.web.dom.Code {
                org.jetbrains.compose.web.dom.Text("shildik apply")
            }
            org.jetbrains.compose.web.dom.Text(".")
        }
        org.jetbrains.compose.web.dom.Li {
            org.jetbrains.compose.web.dom.B {
                org.jetbrains.compose.web.dom.Text("Key rotation without downtime")
            }
            org.jetbrains.compose.web.dom.Text(": several signing keys live side by side, JWKS serves them all, and a retiring key stays for a day because that is how long consumers cache it.")
        }
    }
    org.jetbrains.compose.web.dom.H2(attrs = { id("assemble-one") }) {
        org.jetbrains.compose.web.dom.Text("Assemble one")
    }
    org.jetbrains.compose.web.dom.Pre { org.jetbrains.compose.web.dom.Code(attrs = { classes("language-kotlin") }) { org.jetbrains.compose.web.dom.Text("""val server = shildikServer(
    config = ShildikConfig(issuer = "https://id.example.com", /* … */),
    storage = { config -> sqlx4kStorageModule(config.jdbcUrl, config.dbUser, config.dbPassword) },
)
server.start(wait = true)
""") } }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("Sign-in methods are handed to the graph the same way — by listing them. The libraries a consuming service needs are published from the same repository:")
    }
    org.jetbrains.compose.web.dom.Pre { org.jetbrains.compose.web.dom.Code(attrs = { classes("language-kotlin") }) { org.jetbrains.compose.web.dom.Text("""repositories { maven("https://reposilite.kotlin.website/snapshots") }

dependencies {
    implementation("io.github.youndie.shildik:oidc-auth-server:${'$'}version")  // verify tokens
    implementation("io.github.youndie.shildik:oidc-auth-client:${'$'}version")  // ask for one
}
""") } }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("The validator checks the signature and the expiry and fetches JWKS itself. ")
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("iss")
        }
        org.jetbrains.compose.web.dom.Text(" is deliberately ")
        org.jetbrains.compose.web.dom.B {
            org.jetbrains.compose.web.dom.Text("not")
        }
        org.jetbrains.compose.web.dom.Text(" checked: during a provider migration a service has to accept tokens from both, and two key sources live side by side until the old one is gone.")
    }
    org.jetbrains.compose.web.dom.H2(attrs = { id("status") }) {
        org.jetbrains.compose.web.dom.Text("Status")
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("In production. Shop owners sign in through it, every platform service takes tokens and keys from it, and it has already been through a user migration off Keycloak, a move to Kotlin/Native and a PostgreSQL major upgrade. What is new is the packaging — the module boundaries and the published API may still move.")
    }
}
